import { useMemo, useState } from 'react';
import {
  paymentTerminalsService,
  PaymentTerminalView,
} from '../../services/payment-terminals/payment-terminals.service';
import {
  useAdminPaymentTerminals,
  useAdminTenants,
} from '../../contexts/AdminEventsProvider';
import {
  INITIAL_PINPAD,
  INITIAL_TERMINAL_FORM,
  type PinpadFormState,
  type TerminalCompanyChoice,
  type TerminalFormState,
} from './terminals-page.types';
import {
  buildCreatePayload,
  buildEditDraftFromTerminal,
  buildUpdatePayload,
} from './terminals-page.utils';

// Summary: Owns TerminalsPage state + create/edit/pinpad handlers over the shared admin SSE snapshot.
// Details: docs/function-library/web/contexts-pages.md#terminals-page
export function useTerminalsPageController() {
  const {
    paymentTerminals,
    isLoading: isLoadingTerminals,
    error: bootstrapError,
  } = useAdminPaymentTerminals();
  const { tenants: allTenants } = useAdminTenants();

  const companyNameByTenantId = useMemo(() => {
    const map = new Map<string, string>();
    allTenants.forEach((tenant) => map.set(tenant.tenantId, tenant.companyName));
    return map;
  }, [allTenants]);

  const companies = useMemo<TerminalCompanyChoice[]>(
    () =>
      allTenants
        .filter((tenant) => tenant.isActive)
        .map((tenant) => ({
          tenantId: tenant.tenantId,
          companyName: tenant.companyName,
          isActive: tenant.isActive,
        })),
    [allTenants],
  );

  const [form, setForm] = useState<TerminalFormState>(INITIAL_TERMINAL_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingTerminalId, setEditingTerminalId] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [expandedTerminalId, setExpandedTerminalId] = useState<number | null>(null);

  const displayedError = error ?? bootstrapError;
  const terminalsCountLabel = useMemo(
    () => `סה"כ ${paymentTerminals.length} מסופים`,
    [paymentTerminals.length],
  );
  const isEditing = editingTerminalId !== null;
  const modalTitle = isEditing ? 'עריכת מסוף' : 'צור מסוף חדש';
  const submitLabel = isEditing ? 'שמור' : 'צור';

  const resetForm = () => {
    setForm(INITIAL_TERMINAL_FORM);
    setEditingTerminalId(null);
  };

  const openCreateModal = () => {
    resetForm();
    setError(null);
    setForm({
      ...INITIAL_TERMINAL_FORM,
      tenantId: companies[0]?.tenantId ?? '',
      pinpads: [{ ...INITIAL_PINPAD }],
    });
    setIsModalOpen(true);
  };

  const openEditModal = (terminal: PaymentTerminalView) => {
    setError(null);
    setEditingTerminalId(terminal.id);
    setForm(buildEditDraftFromTerminal(terminal));
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const onChangeForm = <K extends keyof TerminalFormState>(key: K, value: TerminalFormState[K]) => {
    setForm((previous) => ({ ...previous, [key]: value }));
  };

  const onChangePinpad = <K extends keyof PinpadFormState>(
    index: number,
    key: K,
    value: PinpadFormState[K],
  ) => {
    setForm((previous) => ({
      ...previous,
      pinpads: previous.pinpads.map((pinpad, current) =>
        current === index ? { ...pinpad, [key]: value } : pinpad,
      ),
    }));
  };

  const onAddPinpad = () => {
    setForm((previous) => ({ ...previous, pinpads: [...previous.pinpads, { ...INITIAL_PINPAD }] }));
  };

  const onRemovePinpad = (index: number) => {
    setForm((previous) => ({
      ...previous,
      pinpads:
        previous.pinpads.length <= 1
          ? previous.pinpads
          : previous.pinpads.filter((_, current) => current !== index),
    }));
  };

  // Summary: Validates the draft then routes to create/update; canonical row returns via admin SSE.
  // Details: docs/function-library/web/contexts-pages.md#terminals-page
  const handleSubmitTerminal = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);

    if (!isEditing && form.tenantId.trim().length === 0) {
      setError('יש לבחור חברה');
      return;
    }
    if (form.pinpads.every((pinpad) => pinpad.pinpadId.trim().length === 0)) {
      setError('יש להגדיר לפחות פינפד אחד');
      return;
    }

    setIsSubmitting(true);
    try {
      if (editingTerminalId !== null) {
        await paymentTerminalsService.update(
          form.tenantId,
          editingTerminalId,
          buildUpdatePayload(form),
        );
      } else {
        await paymentTerminalsService.create(form.tenantId, buildCreatePayload(form));
      }
      closeModal();
    } catch (submitError: unknown) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : editingTerminalId !== null
            ? 'עדכון המסוף נכשל'
            : 'יצירת המסוף נכשלה',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const onToggleExpanded = (terminalId: number) => {
    setExpandedTerminalId((current) => (current === terminalId ? null : terminalId));
  };

  return {
    terminals: paymentTerminals,
    isLoadingTerminals,
    companies,
    companyNameByTenantId,
    form,
    isSubmitting,
    isModalOpen,
    expandedTerminalId,
    displayedError,
    terminalsCountLabel,
    isEditing,
    modalTitle,
    submitLabel,
    openCreateModal,
    openEditModal,
    closeModal,
    handleSubmitTerminal,
    onChangeForm,
    onChangePinpad,
    onAddPinpad,
    onRemovePinpad,
    onToggleExpanded,
  };
}
